import { jsx as _jsx } from "react/jsx-runtime";
// DOCK_SSOT_B3 (RD-03a): shell dùng chung single↔multi — tách từ
// WorkspaceRightDockSlot (:112-254). Sở hữu: wrapper flyout + grip (CSS) +
// open/pin/lastTab + persist keys + tier gate + Suspense + lazy host.
// Multi đi qua WorkspaceRightDockSlot (data-bridging tầng trên); single (S2)
// mount trực tiếp file này với cùng bộ props dữ liệu.
import React, { lazy, Suspense, useState } from "react";
import { resolveDockAccess } from "./dockAccess";
import { persistDock, readDockPin, readPersistedOpen, readPersistedTab, writeDockPin, } from "./RightSidebarDockHost";
import { DockUpgradeNudge } from "./DockUpgradeNudge";
import { useSidebarResize } from "../../hooks/overlays/useSidebarResize";
import { useMobileViewportMatch } from "../../hooks/useMobileViewportMatch";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import railStyles from "../../styles/right-dock-rail.module.css";
// Tab panel hợp lệ cho lastTab (loại alerts action-tab).
const PANEL_TABS = ["watchlist", "dataWindow", "objects", "tape", "advisory", "orderTicket"];
function readLastTab() {
    const raw = readPersistedTab();
    if (raw && PANEL_TABS.includes(raw))
        return raw;
    return "watchlist";
}
/** D1: tab cuối đã lưu, chỉ khi open ≠ "false" (chưa từng lưu ⇒ strip-only). */
function readRestoreTab() {
    if (!readPersistedOpen())
        return null;
    const raw = readPersistedTab();
    return raw && PANEL_TABS.includes(raw) ? raw : null;
}
const LazyRightSidebarDockHost = lazy(() => import("./RightSidebarDockHost").then((m) => ({ default: m.RightSidebarDockHost })));
export function RightDockRail(props) {
    const { dockTierGate, tier, symbol, timeframe, datum, onSymbolChange, watchlistAdapter, drawingDockNode, tradingDockNode, tape, advisory, onAlertsOpen, customPanelNode, isMobile, restoreOpenOnMount, } = props;
    // Width persist reuse (cap window-based đúng ở workspace level).
    const { t } = useWidgetI18n();
    const { sidebarWidth, isResizingSidebar, handleSidebarPointerDown, } = useSidebarResize();
    // F-UI-04: single standalone/demo không truyền `isMobile`; suy ra từ đúng
    // query CSS (max-width:767px) để pin desktop-only trên surface thật. Hook
    // gọi vô điều kiện trước mọi early-return (rules-of-hooks).
    const mobileMatch = useMobileViewportMatch();
    const effMobile = isMobile ?? mobileMatch;
    // F1 autohide: trượt/hiện thuần CSS (:hover/:focus-within trên wrapper).
    // React chỉ sở hữu openTab (mở khi CLICK tab, đóng khi X/toggle/Escape),
    // pinned (in-flow) và lastTab. Không hover-open, không timer, không sticky.
    const [openTab, setOpenTab] = useState(() => restoreOpenOnMount && !isMobile ? readRestoreTab() : null);
    const [lastTab, setLastTab] = useState(() => readLastTab());
    const [pinned, setPinned] = useState(() => readDockPin());
    const effPinned = pinned && !effMobile;
    const tierFull = resolveDockAccess(tier, customPanelNode != null ? false : dockTierGate).mode === "full";
    // Fly = overlay thuần (mirror drawing toolbar flyout): KHÔNG reflow gì,
    // panel giữ full width (drawer phủ tạm, đóng để xem hết). Chỉ docked/pinned
    // mới đẩy layout qua flex (mainCol co, panel co theo — đã đo 1600→1295).
    // F-UI-04: X/Escape/tab-active chỉ đóng panel; dock pin sống tới khi người
    // dùng bấm Undock (masterplan Phase 3 — trước đây nhánh này tự unpin).
    const handleOpenTabChange = (tab) => {
        if (tab) {
            setOpenTab(tab);
            setLastTab(tab);
            persistDock(tab, true);
        }
        else {
            setOpenTab(null);
            persistDock(lastTab, false);
        }
    };
    // F-UI-04: pin rỗng = strip 40px docked, KHÔNG tự mở panel (AC Phase 3).
    const handlePinChange = (next) => {
        setPinned(next);
        writeDockPin(next);
    };
    // F1: cả nudge lẫn dock đều nằm trong flyout (trượt thuần CSS, mirror
    // drawing toolbar shell). Wrapper 10px = grip zone; openTab != null giữ
    // flyout trượt ra qua class workspaceDockOpen.
    const flyoutOpenCls = openTab !== null ? ` ${railStyles.workspaceDockOpen}` : "";
    // Tier-bypass mirror single (VNInvestCharts:2689): host có custom panel
    // thì full dock bất kể tier.
    if (!tierFull) {
        return (_jsx("div", { className: railStyles.workspaceDock, children: _jsx("div", { className: railStyles.workspaceDockFlyout, children: _jsx(DockUpgradeNudge, {}) }) }));
    }
    return (_jsx(Suspense, { fallback: _jsx("div", { style: { width: 40, alignSelf: "stretch", borderLeft: "1px solid var(--vnsc-border, rgba(255,255,255,0.08))" }, "aria-hidden": "true" }), children: _jsx("div", { className: `${railStyles.workspaceDock}${effPinned ? ` ${railStyles.workspaceDockPinned}` : ""}${flyoutOpenCls}`, tabIndex: 0, role: "complementary", "aria-label": t("dock.toggleRail"), children: _jsx("div", { className: railStyles.workspaceDockFlyout, children: _jsx(LazyRightSidebarDockHost, { width: sidebarWidth, isResizing: isResizingSidebar, onSplitterPointerDown: handleSidebarPointerDown, symbol: symbol, timeframe: timeframe, datum: datum, onSymbolChange: onSymbolChange, watchlistAdapter: watchlistAdapter, drawingDockNode: drawingDockNode, tradingDockNode: tradingDockNode, tape: tape, advisory: advisory, onAlertsOpen: onAlertsOpen, customPanelNode: customPanelNode, openTab: openTab, onOpenTabChange: handleOpenTabChange, showPinToggle: !effMobile, pinned: effPinned, onPinChange: handlePinChange }) }) }) }));
}
